import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useToast } from '../context/ToastContext.jsx'
import { useGeneralSettings } from '../hooks/useGeneralSettings'
import { formatRupees, scheduleLabel, pujaDateInfo, formatTime, featureIcon } from '../services/pujaFormat.js'
import { saveBookingDraft } from '../services/poojaBookingDraft.js'
import PoojaCard, { PoojaImageFallback } from '../components/pooja/PoojaCard.jsx'

/** wa.me wants digits; bare 10-digit Indian numbers get the 91 prefix. */
const toDigits = (raw = '') => {
  const digits = raw.replace(/\D/g, '')
  return digits.length === 10 ? `91${digits}` : digits
}
const FALLBACK_CONTACT_NUMBER = '919091840899'
const val = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '')

/** Free-text guidance blocks from the puja record — rendered only when the
 * admin filled them in. */
const INFO_BLOCKS = [
  { key: 'benefits', icon: 'fa-wand-magic-sparkles', title: 'Benefits' },
  { key: 'who_should_perform', icon: 'fa-user-check', title: 'Who Should Perform' },
  { key: 'what_devotees_receive', icon: 'fa-gift', title: 'What You Receive' },
  { key: 'precautions', icon: 'fa-triangle-exclamation', title: 'Precautions' },
  { key: 'instructions', icon: 'fa-list-check', title: 'Instructions' },
]

export default function PoojaDetails() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { settings } = useGeneralSettings()
  const waNumber = toDigits(val(settings.whatsapp) || val(settings.phone)) || FALLBACK_CONTACT_NUMBER

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pujaDetails', slug],
    queryFn: () => api.puja.bySlug(slug),
    retry: false,
  })

  const puja = data?.success ? data.data : null

  // "Devotees also booked" — other pujas from the same category.
  const { data: relatedData, isLoading: relatedLoading } = useQuery({
    queryKey: ['pujaRelated', puja?.category?.id],
    queryFn: () => api.puja.list({ category: puja.category.id, limit: 5 }),
    enabled: Boolean(puja?.category?.id),
    staleTime: 5 * 60 * 1000,
  })
  const related = (relatedData?.data ?? []).filter((p) => p.slug !== slug).slice(0, 4)

  const gallery = useMemo(() => {
    if (!puja) return []
    return [...new Set([puja.image, ...(puja.gallery ?? [])].filter(Boolean))]
  }, [puja])

  const [activeImage, setActiveImage] = useState(0)
  const [lightbox, setLightbox] = useState(false)
  const [failedImages, setFailedImages] = useState(() => new Set())
  const [packageId, setPackageId] = useState(null)
  const [openFaq, setOpenFaq] = useState(0)

  useEffect(() => {
    setActiveImage(0)
    setPackageId(null)
    setOpenFaq(0)
  }, [slug])

  if (isLoading) return <DetailsSkeleton />

  if (isError || !puja) {
    return (
      <div className="flex flex-col min-h-screen bg-background items-center justify-center px-6 pt-28 pb-20 text-center normal-case select-none">
        <i className="fa-solid fa-hands-praying text-4xl text-primary/20"></i>
        <h1 className="mt-6 text-2xl font-heading text-primary uppercase">Pooja not found</h1>
        <p className="mt-3 text-sm text-gray-500 font-body max-w-md">
          This ritual may have concluded or the link is incorrect. Explore all upcoming poojas instead.
        </p>
        <Link
          to="/pooja-energization"
          className="mt-8 inline-flex items-center gap-2 px-8 h-12 rounded-xl bg-primary text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-secondary transition-all"
        >
          <i className="fa-solid fa-arrow-left"></i> All Poojas
        </Link>
      </div>
    )
  }

  const { weekday, label: dateLabel, daysLeft } = pujaDateInfo(puja.puja_date)
  const packages = puja.packages ?? []
  // Default to the admin-flagged "Most Popular" package until a pick is made.
  const selectedPackage =
    packages.find((p) => p.id === packageId) ??
    packages.find((p) => p.is_most_popular) ??
    packages[0] ??
    null
  const hasDiscount = selectedPackage && selectedPackage.final_price < selectedPackage.original_price
  const templePlace = [puja.temple?.location, puja.temple?.state].filter(Boolean).join(', ')
  const infoBlocks = INFO_BLOCKS.filter((b) => val(puja[b.key]))

  const bookNow = () => {
    if (!selectedPackage) {
      showToast('No packages are available for this pooja yet')
      return
    }
    saveBookingDraft({
      puja: {
        id: puja.id,
        slug: puja.slug,
        title: puja.title,
        subtitle: puja.subtitle,
        image: puja.image,
        category: puja.category ? { id: puja.category.id, title: puja.category.title, slug: puja.category.slug } : null,
        temple: puja.temple ? { name: puja.temple.name, location: puja.temple.location, state: puja.temple.state } : null,
        schedule_type: puja.schedule_type,
        festival_name: puja.festival_name,
        puja_date: puja.puja_date,
        puja_time: puja.puja_time,
      },
      package: {
        id: selectedPackage.id,
        title: selectedPackage.title,
        subtitle: selectedPackage.subtitle,
        max_devotees: selectedPackage.max_devotees,
        original_price: selectedPackage.original_price,
        discounted_price: selectedPackage.discounted_price,
        final_price: selectedPackage.final_price,
        offer_percentage: selectedPackage.offer_percentage,
      },
    })
    showToast(`${selectedPackage.title} package selected — complete your sankalp details`)
    navigate('/pooja-energization/checkout')
  }

  const chevron = (
    <svg className="w-3 h-3 shrink-0 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="m9 18 6-6-6-6"></path>
    </svg>
  )

  const galleryImage = (src, cls) =>
    !src || failedImages.has(src) ? (
      <PoojaImageFallback className={cls} />
    ) : (
      <img
        src={src}
        alt={puja.title}
        className={cls}
        onError={() => setFailedImages((prev) => new Set(prev).add(src))}
      />
    )

  return (
    <div className="flex flex-col min-h-screen bg-background font-normal uppercase select-none">
      {/* TOP: BREADCRUMB + GALLERY + BOOKING PANEL */}
      <div className="bg-white pt-28 md:pt-32 pb-10 md:pb-14 border-b border-gray-200">
        <div className="container px-6 mx-auto">
          <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-normal uppercase tracking-[0.3em] text-gray-400">
            <Link to="/" className="hover:text-secondary transition-colors">HOME</Link>
            <span className="flex items-center gap-3">
              {chevron}
              <Link to="/pooja-energization" className="hover:text-secondary transition-colors">POOJA ENERGIZATION</Link>
            </span>
            <span className="flex min-w-0 items-center gap-3">
              {chevron}
              <span className="truncate text-secondary normal-case">{puja.title}</span>
            </span>
          </nav>

          <section className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* Gallery */}
            <div className="lg:col-span-6 space-y-4">
              <div className="flex gap-4">
                {gallery.length > 1 && (
                  <div className="hidden sm:flex flex-col gap-3 w-20 shrink-0">
                    {gallery.map((img, index) => (
                      <button
                        key={img}
                        type="button"
                        onClick={() => setActiveImage(index)}
                        className={`relative aspect-square rounded-lg overflow-hidden border transition-all duration-300 ${activeImage === index
                          ? 'border-secondary ring-2 ring-secondary/10'
                          : 'border-border hover:border-primary/40'
                          }`}
                      >
                        {galleryImage(img, 'object-cover w-full h-full')}
                      </button>
                    ))}
                  </div>
                )}

                <div className="relative flex-1 aspect-square rounded-xl overflow-hidden border border-border/60 bg-white group">
                  {galleryImage(gallery[activeImage] ?? gallery[0], 'object-cover w-full h-full transition-all duration-500')}

                  <div className="absolute top-4 left-4 z-10">
                    <span className="bg-primary text-white px-3 py-1 rounded-lg text-[9px] font-bold tracking-widest uppercase shadow-lg">
                      <i className="fa-solid fa-calendar-day mr-1.5 text-secondary"></i>{scheduleLabel(puja)}
                    </span>
                  </div>

                  <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => setLightbox(true)}
                      className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-md border border-border flex items-center justify-center hover:bg-secondary hover:text-white transition-all shadow-sm"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <polyline points="9 21 3 21 3 15"></polyline>
                        <line x1="21" x2="14" y1="3" y2="10"></line>
                        <line x1="3" x2="10" y1="21" y2="14"></line>
                      </svg>
                    </button>
                  </div>

                  {gallery.length > 1 && (
                    <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4">
                      <button
                        onClick={() => setActiveImage((i) => (i > 0 ? i - 1 : gallery.length - 1))}
                        className="w-10 h-10 rounded-full bg-white/90 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-secondary hover:text-white transition-all"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"></path></svg>
                      </button>
                      <button
                        onClick={() => setActiveImage((i) => (i < gallery.length - 1 ? i + 1 : 0))}
                        className="w-10 h-10 rounded-full bg-white/90 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-secondary hover:text-white transition-all"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6"></path></svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* What every booking includes */}
              <div className="p-5 rounded-xl bg-white border border-gray-200 space-y-4">
                <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-gray-400">Included with every booking</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'fa-video', text: 'Full pooja video delivered' },
                    { icon: 'fa-hands-praying', text: 'Sankalp with Name & Gotra' },
                    { icon: 'fa-file-lines', text: 'Booking invoice & certificate' },
                    { icon: 'fa-user-check', text: 'Experienced Vedic pandits' },
                  ].map((f) => (
                    <div key={f.text} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center text-secondary border border-amber-100 shrink-0">
                        <i className={`fa-solid ${f.icon} text-sm`}></i>
                      </div>
                      <p className="text-[11px] text-gray-700 uppercase tracking-wider leading-snug">{f.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Booking panel */}
            <div className="lg:col-span-6 space-y-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {puja.category?.title && (
                    <span className="bg-gray-900 text-white px-3 py-1 rounded-lg text-[9px] uppercase tracking-widest inline-block">{puja.category.title}</span>
                  )}
                  <span className="bg-secondary/10 text-secondary border border-secondary/20 px-3 py-1 rounded-lg text-[9px] uppercase tracking-widest inline-block font-bold">{scheduleLabel(puja)}</span>
                </div>
                <h1 className="text-2xl md:text-3xl text-gray-900 tracking-tight normal-case font-heading leading-snug">{puja.title}</h1>
                {puja.subtitle && (
                  <p className="text-sm text-secondary font-semibold normal-case">{puja.subtitle}</p>
                )}

                {/* Temple + date */}
                <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-dashed border-gray-200 normal-case">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-primary/5 flex items-center justify-center text-primary shrink-0">
                      <i className="fa-solid fa-gopuram text-sm"></i>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-widest text-gray-400">Temple</p>
                      <p className="text-xs font-semibold text-gray-900 leading-snug">{puja.temple?.name ?? '—'}</p>
                      {templePlace && <p className="text-[10px] text-gray-500">{templePlace}</p>}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-primary/5 flex items-center justify-center text-primary shrink-0">
                      <i className="fa-solid fa-calendar-check text-sm"></i>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-widest text-gray-400">Pooja Schedule</p>
                      <p className="text-xs font-semibold text-gray-900 leading-snug">
                        {dateLabel ? `${weekday}, ${dateLabel}` : scheduleLabel(puja)}
                        {puja.puja_time ? ` · ${formatTime(puja.puja_time)}` : ''}
                      </p>
                      {daysLeft != null && daysLeft >= 0 && (
                        <p className="text-[10px] text-secondary font-bold">
                          {daysLeft > 1 ? `Bookings close in ${daysLeft} days` : 'Last day to book — closing soon'}
                        </p>
                      )}
                      {puja.cutoff_time && (
                        <p className="text-[9px] text-gray-400">Booking cut-off: {formatTime(puja.cutoff_time)}</p>
                      )}
                    </div>
                  </div>
                </div>

                {puja.short_description && (
                  <p className="text-sm text-gray-600 leading-relaxed normal-case pt-1">{puja.short_description}</p>
                )}
              </div>

              {/* Package selection */}
              <div className="space-y-3 border-t border-dashed border-gray-200 pt-4">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Choose Your Sankalp Package *</p>
                {packages.length === 0 ? (
                  <p className="text-xs text-gray-500 normal-case">Packages for this pooja are being prepared — check back shortly.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {packages.map((pkg) => {
                      const active = selectedPackage && pkg.id === selectedPackage.id
                      const discounted = pkg.final_price < pkg.original_price
                      return (
                        <div
                          key={pkg.id}
                          onClick={() => setPackageId(pkg.id)}
                          className={`relative cursor-pointer text-left rounded-2xl border p-4 transition-all duration-300 ${active
                            ? 'border-secondary bg-secondary/5 ring-2 ring-secondary/15 shadow-md'
                            : 'border-gray-200 bg-white hover:border-secondary/40'
                            }`}
                        >
                          {(pkg.is_most_popular || pkg.is_best_value) && (
                            <span className="absolute -top-2.5 left-4 rounded-full bg-secondary px-3 py-0.5 text-[8px] font-bold uppercase tracking-widest text-white shadow">
                              {pkg.is_most_popular ? 'Most Popular' : 'Best Value'}
                            </span>
                          )}
                          <div>
                            <p className="text-xs font-bold text-primary uppercase tracking-wide leading-snug">{pkg.title}</p>
                            {pkg.subtitle && <p className="text-[9px] text-gray-500 normal-case mt-0.5">{pkg.subtitle}</p>}
                          </div>
                          <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                            <span className="text-lg font-bold text-primary">{formatRupees(pkg.final_price)}</span>
                            {discounted && (
                              <>
                                <span className="text-[10px] text-gray-400 line-through">{formatRupees(pkg.original_price)}</span>
                                {pkg.offer_percentage != null && (
                                  <span className="text-[8px] font-bold text-white bg-secondary rounded px-1.5 py-0.5">{pkg.offer_percentage}% OFF</span>
                                )}
                              </>
                            )}
                          </div>
                          <p className="text-[9px] text-gray-500 normal-case mt-1">
                            <i className="fa-solid fa-users text-secondary mr-1"></i>
                            Up to {pkg.max_devotees} devotee{pkg.max_devotees > 1 ? 's' : ''} in sankalp
                          </p>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Selected package features */}
                {selectedPackage?.features?.length > 0 && (
                  <ul className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4 grid sm:grid-cols-2 gap-2">
                    {selectedPackage.features.map((f) => (
                      <li key={f.id} className="flex items-start gap-2.5 text-[11px] text-gray-600 normal-case leading-relaxed">
                        <i className={`fa-solid ${featureIcon(f.icon)} mt-0.5 text-[10px] text-secondary`}></i>
                        {f.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Total + actions */}
              {selectedPackage && (
                <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.2em] text-gray-400">Total Seva Amount</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl text-red-600 font-medium">{formatRupees(selectedPackage.final_price)}</span>
                        {hasDiscount && (
                          <span className="text-[10px] text-gray-400 normal-case">
                            You save {formatRupees(selectedPackage.original_price - selectedPackage.final_price)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right normal-case">
                      <p className="text-[9px] text-gray-400 uppercase tracking-widest">{selectedPackage.title}</p>
                      <p className="text-[10px] text-gray-600">{selectedPackage.max_devotees} devotee{selectedPackage.max_devotees > 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={bookNow}
                      className="flex-1 h-12 rounded-lg bg-secondary text-white hover:bg-gray-900 transition-all uppercase text-[11px] tracking-[0.2em] shadow-sm flex items-center justify-center gap-3"
                    >
                      Book This Pooja
                      <i className="fa-solid fa-arrow-right text-xs"></i>
                    </button>
                    <a
                      href={`https://wa.me/${waNumber}?text=${encodeURIComponent(`Namaste 🙏 I want to know more about: ${puja.title}${puja.temple?.name ? ` (${puja.temple.name})` : ''}`)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1 h-12 rounded-lg border border-gray-200 text-gray-700 hover:border-green-500 hover:text-green-600 transition-all uppercase text-[11px] tracking-[0.2em] flex items-center justify-center gap-3"
                    >
                      <i className="fa-brands fa-whatsapp text-base"></i>
                      Ask on WhatsApp
                    </a>
                  </div>
                  <p className="text-[9px] text-gray-400 normal-case flex items-center gap-2">
                    <i className="fa-solid fa-shield-halved text-secondary"></i>
                    Secure payment via Razorpay · Invoice generated on confirmation
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* ABOUT + GUIDANCE */}
      <section className="container mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 space-y-6">
            {val(puja.description) && (
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 tracking-tight uppercase">About This Pooja</h2>
                <div
                  className="text-sm text-gray-600 mt-3 leading-relaxed normal-case [&_p]:mb-3"
                  dangerouslySetInnerHTML={{ __html: puja.description }}
                />
              </div>
            )}
            {puja.temple && (
              <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                {puja.temple.image && (
                  <div className="h-44 overflow-hidden">
                    {galleryImage(puja.temple.image, 'object-cover w-full h-full')}
                  </div>
                )}
                <div className="p-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center text-primary">
                      <i className="fa-solid fa-gopuram"></i>
                    </div>
                    <div className="normal-case">
                      <h3 className="font-heading text-primary uppercase tracking-tight text-base">{puja.temple.name}</h3>
                      {(templePlace || puja.temple.country) && (
                        <p className="text-[11px] text-gray-500">
                          {[templePlace, puja.temple.country].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {infoBlocks.length > 0 && (
            <div className="lg:col-span-5">
              <h2 className="text-2xl font-semibold text-gray-900 tracking-tight uppercase mb-4">Pooja Guidance</h2>
              <div className="space-y-4">
                {infoBlocks.map((b) => (
                  <div key={b.key} className="rounded-2xl border border-gray-200 bg-white p-5 hover:border-secondary/40 hover:shadow-md transition-all duration-300">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center text-secondary shrink-0">
                        <i className={`fa-solid ${b.icon}`}></i>
                      </div>
                      <p className="text-xs font-bold text-primary uppercase tracking-wide">{b.title}</p>
                    </div>
                    <p className="mt-3 text-[11px] text-gray-500 normal-case leading-relaxed">{puja[b.key]}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      {puja.faqs?.length > 0 && (
        <section className="container mx-auto px-6 pb-12">
          <h2 className="text-2xl font-semibold text-gray-900 tracking-tight uppercase mb-6">Frequently Asked Questions</h2>
          <div className="space-y-3 max-w-3xl">
            {puja.faqs.map((f, i) => {
              const isOpen = openFaq === i
              return (
                <div key={f.id ?? f.question} className={`rounded-2xl border transition-all ${isOpen ? 'bg-white border-secondary/40 shadow-sm' : 'bg-white border-gray-200 hover:border-secondary/30'}`}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="text-xs md:text-sm font-semibold text-primary uppercase leading-snug">{f.question}</span>
                    <i className={`fa-solid fa-chevron-down text-xs text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {isOpen && <p className="px-5 pb-5 -mt-1 text-xs text-gray-500 normal-case leading-relaxed">{f.answer}</p>}
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* RELATED POOJAS */}
      {(relatedLoading || related.length > 0) && (
        <section className="py-12 bg-white border-t border-gray-200">
          <div className="container px-6 mx-auto">
            <div className="max-w-2xl mx-auto text-center space-y-3 mb-10">
              <div className="flex items-center justify-center gap-3">
                <span className="h-px w-8 bg-secondary/50"></span>
                <span className="text-[10px] uppercase tracking-[0.4em] text-secondary font-bold">Continue Your Seva</span>
                <span className="h-px w-8 bg-secondary/50"></span>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl uppercase leading-tight text-primary">
                Devotees Also <span className="text-gradient-gold font-bold">Booked</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedLoading
                ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="rounded-3xl border border-gray-100 overflow-hidden animate-pulse">
                    <div className="aspect-4/3 bg-gray-200" />
                    <div className="p-6 space-y-3">
                      <div className="h-4 w-3/4 rounded bg-gray-200" />
                      <div className="h-3 w-1/2 rounded bg-gray-100" />
                    </div>
                  </div>
                ))
                : related.map((p) => <PoojaCard key={p.id} pooja={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightbox && gallery.length > 0 && (
        <div
          onClick={() => setLightbox(false)}
          className="fixed inset-0 z-300 bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
        >
          <img src={gallery[activeImage] ?? gallery[0]} alt={puja.title} className="max-h-[90vh] max-w-[90vw] object-contain rounded-xl" />
          <button
            type="button"
            onClick={() => setLightbox(false)}
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white/20 transition-all"
          >
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>
      )}
    </div>
  )
}

function DetailsSkeleton() {
  return (
    <div className="flex flex-col min-h-screen bg-background select-none">
      <div className="bg-white pt-28 md:pt-32 pb-10 md:pb-14 border-b border-gray-200">
        <div className="container px-6 mx-auto animate-pulse">
          <div className="mb-6 h-3 w-64 rounded bg-gray-100" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-6 flex gap-4">
              <div className="hidden sm:flex flex-col gap-3 w-20 shrink-0">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="aspect-square rounded-lg bg-gray-200" />
                ))}
              </div>
              <div className="flex-1 aspect-square rounded-xl bg-gray-200" />
            </div>
            <div className="lg:col-span-6 space-y-6">
              <div className="h-5 w-40 rounded-lg bg-gray-200" />
              <div className="h-8 w-3/4 rounded bg-gray-200" />
              <div className="h-4 w-56 rounded bg-gray-100" />
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-28 rounded-2xl bg-gray-100" />
                ))}
              </div>
              <div className="h-24 rounded-2xl bg-gray-100" />
              <div className="h-14 rounded-lg bg-gray-200" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
